import React, { useEffect, useMemo } from 'react';

import type { MediumProps } from 'src/components/other/MediumComponent';
import type { GlobalAudioContent } from 'src/contexts/AudioProvider';
import useAudio from 'src/hooks/useAudio';
import type { Medium } from 'src/types/__generated__/graphql';
import AudioPlayer from 'src/components/ui/AudioPlayer';

interface GlobalAudioMediumProps extends MediumProps {
    thumbnail?: Medium;
    content?: GlobalAudioContent;
    title?: string;
}

const GlobalAudio: React.FC<GlobalAudioMediumProps> = ({ media, thumbnail = null, content, title = 'Audio' }) => {
    const {
        isPlaying,
        play,
        pause,
        updateCurrentTime,
        setVolume,
        unmute,
        mute,
        muted,
        volume,
        timeSliderValue,
        currentTime,
        setTimeSliderValue,
        medium: globalAudioMedium,
        changeAudioAndPlay,
        openIfMediumPlayed,
        close,
    } = useAudio();

    useEffect(
        () => () => {
            openIfMediumPlayed(media.url);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    useEffect(() => {
        if (globalAudioMedium && globalAudioMedium.url === media.url) {
            close(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media.url, globalAudioMedium]);

    const isMediaInAudioProvider = useMemo(
        () => globalAudioMedium && globalAudioMedium.url === media.url,
        [globalAudioMedium, media.url],
    );

    // if the media is the media in audio provider, use the provider's values
    const sliderValue = isMediaInAudioProvider ? timeSliderValue : 0;
    const currentTimeValue = isMediaInAudioProvider ? currentTime : 0;
    const playing = isMediaInAudioProvider ? isPlaying : false;
    const vol = isMediaInAudioProvider ? volume : 10;
    const isMuted = isMediaInAudioProvider ? muted : false;

    const disabledControls = globalAudioMedium && !isMediaInAudioProvider;

    const playPauseHandle = async () => {
        if (playing) {
            pause();
        } else {
            if (!isMediaInAudioProvider) {
                changeAudioAndPlay({
                    medium: media,
                    title,
                    content,
                });
            } else {
                await play();
            }
        }
    };

    const handleMuteClick = () => {
        if (isMediaInAudioProvider) {
            if (muted) {
                unmute();
            } else {
                mute();
            }
        }
    };

    return (
        <AudioPlayer
            thumbnailUrl={thumbnail?.url}
            onPlayPause={playPauseHandle}
            playing={playing}
            onTimeSliderChangeCommitted={updateCurrentTime}
            onTimeSliderChange={setTimeSliderValue}
            timeSliderValue={sliderValue}
            onVolumeChange={setVolume}
            volume={vol}
            onMute={handleMuteClick}
            isMuted={isMuted}
            currentTime={currentTimeValue}
            totalDuration={media.duration}
            disabledControls={disabledControls}
        />
    );
};

export default GlobalAudio;
